'use client'
import React from 'react'
import { createModal } from '@gluestack-ui/core/modal/creator'
import { Pressable, View, ScrollView, ViewStyle } from 'react-native'
import {
    Motion,
    AnimatePresence,
    createMotionAnimatedComponent,
    MotionComponentProps,
} from '@legendapp/motion'
import { tva } from '../../tailwind/merge'
import { withStyleContext, useStyleContext } from '@gluestack-ui/utils/nativewind-utils'
import { cssInterop } from 'nativewind'
import type { VariantProps } from '@gluestack-ui/utils/nativewind-utils'

// The gluestack Drawer is `createModal` (same overlay/focus/dismiss machinery as
// `../modal`) with edge-anchored positioning + a slide-in animation instead of the
// modal's centred scale. Kept structure-only here (gluestack default tokens); AOS
// colours/tokens live in `components/drawer/Drawer.tsx`, same split as Button/Input.
const SCOPE = 'DRAWER'

export type DrawerAnchor = 'left' | 'right' | 'top' | 'bottom'
export type DrawerSize = 'sm' | 'md' | 'lg' | 'full'

type IAnimatedPressableProps = React.ComponentProps<typeof Pressable> &
    MotionComponentProps<typeof Pressable, ViewStyle, unknown, unknown, unknown>

const AnimatedPressable = createMotionAnimatedComponent(
    Pressable,
) as React.ComponentType<IAnimatedPressableProps>

type IMotionViewProps = React.ComponentProps<typeof View> &
    MotionComponentProps<typeof View, ViewStyle, unknown, unknown, unknown>

const MotionView = Motion.View as React.ComponentType<IMotionViewProps>

const UIDrawer = createModal({
    Root: withStyleContext(View, SCOPE),
    Backdrop: AnimatedPressable,
    Content: MotionView,
    Body: ScrollView,
    CloseButton: Pressable,
    Footer: View,
    Header: View,
    AnimatePresence: AnimatePresence,
})

cssInterop(AnimatedPressable, { className: 'style' })
cssInterop(MotionView, { className: 'style' })

const drawerStyle = tva({
    base: 'w-full h-full web:pointer-events-none relative',
    variants: {
        size: {
            sm: '',
            md: '',
            lg: '',
            full: '',
        },
        anchor: {
            left: 'items-start',
            right: 'items-end',
            top: 'justify-start',
            bottom: 'justify-end',
        },
    },
})

const drawerBackdropStyle = tva({
    base: 'absolute left-0 top-0 right-0 bottom-0 bg-background-dark web:cursor-default',
})

const drawerContentStyle = tva({
    base: 'bg-background-0 overflow-hidden border border-outline-100 shadow-hard-2 p-6 absolute',
    parentVariants: {
        size: {
            sm: '',
            md: '',
            lg: '',
            full: '',
        },
        anchor: {
            left: 'h-full left-0 top-0',
            right: 'h-full right-0 top-0',
            top: 'w-full top-0 left-0',
            bottom: 'w-full bottom-0 left-0',
        },
    },
    parentCompoundVariants: [
        { anchor: 'left', size: 'sm', class: 'w-1/4' },
        { anchor: 'left', size: 'md', class: 'w-1/2' },
        { anchor: 'left', size: 'lg', class: 'w-3/4' },
        { anchor: 'left', size: 'full', class: 'w-full' },
        { anchor: 'right', size: 'sm', class: 'w-1/4' },
        { anchor: 'right', size: 'md', class: 'w-1/2' },
        { anchor: 'right', size: 'lg', class: 'w-3/4' },
        { anchor: 'right', size: 'full', class: 'w-full' },
        { anchor: 'top', size: 'sm', class: 'h-1/4' },
        { anchor: 'top', size: 'md', class: 'h-1/2' },
        { anchor: 'top', size: 'lg', class: 'h-3/4' },
        { anchor: 'top', size: 'full', class: 'h-full' },
        { anchor: 'bottom', size: 'sm', class: 'h-1/4' },
        { anchor: 'bottom', size: 'md', class: 'h-1/2' },
        { anchor: 'bottom', size: 'lg', class: 'h-3/4' },
        { anchor: 'bottom', size: 'full', class: 'h-full' },
    ],
})

const drawerBodyStyle = tva({ base: 'mt-2 mb-6' })
const drawerCloseButtonStyle = tva({
    base: 'group/drawer-close-button z-10 rounded data-[focus-visible=true]:web:bg-background-100 web:outline-0 cursor-pointer',
})
const drawerHeaderStyle = tva({ base: 'justify-between items-center flex-row' })
const drawerFooterStyle = tva({ base: 'flex-row justify-end items-center' })

type IDrawerProps = React.ComponentProps<typeof UIDrawer> &
    VariantProps<typeof drawerStyle> & { className?: string; anchor?: DrawerAnchor }

type IDrawerBackdropProps = React.ComponentProps<typeof UIDrawer.Backdrop> &
    VariantProps<typeof drawerBackdropStyle> & { className?: string }

type IDrawerContentProps = React.ComponentProps<typeof UIDrawer.Content> &
    VariantProps<typeof drawerContentStyle> & { className?: string }

type IDrawerHeaderProps = React.ComponentProps<typeof UIDrawer.Header> &
    VariantProps<typeof drawerHeaderStyle> & { className?: string }

type IDrawerBodyProps = React.ComponentProps<typeof UIDrawer.Body> &
    VariantProps<typeof drawerBodyStyle> & { className?: string }

type IDrawerFooterProps = React.ComponentProps<typeof UIDrawer.Footer> &
    VariantProps<typeof drawerFooterStyle> & { className?: string }

type IDrawerCloseButtonProps = React.ComponentProps<typeof UIDrawer.CloseButton> &
    VariantProps<typeof drawerCloseButtonStyle> & { className?: string }

const Drawer = React.forwardRef<React.ComponentRef<typeof UIDrawer>, IDrawerProps>(function Drawer(
    { className, size = 'sm', anchor = 'left', ...props },
    ref,
) {
    return (
        <UIDrawer
            ref={ref}
            {...props}
            pointerEvents='box-none'
            className={drawerStyle({ size, anchor, class: className })}
            context={{ size, anchor }}
        />
    )
})

const DrawerBackdrop = React.forwardRef<
    React.ComponentRef<typeof UIDrawer.Backdrop>,
    IDrawerBackdropProps
>(function DrawerBackdrop({ className, ...props }, ref) {
    return (
        <UIDrawer.Backdrop
            ref={ref}
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            exit={{ opacity: 0 }}
            transition={{
                type: 'spring',
                damping: 18,
                stiffness: 250,
                opacity: { type: 'timing', duration: 250 },
            }}
            {...props}
            className={drawerBackdropStyle({ class: className })}
        />
    )
})

const DrawerContent = React.forwardRef<
    React.ComponentRef<typeof UIDrawer.Content>,
    IDrawerContentProps
>(function DrawerContent({ className, size, ...props }, ref) {
    const { size: parentSize, anchor: parentAnchor } = useStyleContext(SCOPE) as {
        size: DrawerSize
        anchor: DrawerAnchor
    }

    return (
        // Opacity fade only. A translateX slide via @legendapp/motion's numeric `x`
        // does NOT tween back to 0 on react-native-web here (the content sticks at the
        // initial offscreen offset — only `opacity`/`scale` animate reliably, as the
        // modal does). The panel is edge-anchored (absolute) so a fade reads fine;
        // a real slide would need a CSS-transition approach (follow-up polish).
        <UIDrawer.Content
            ref={ref}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
                type: 'timing',
                duration: 200,
            }}
            {...props}
            className={drawerContentStyle({
                parentVariants: { size: parentSize, anchor: parentAnchor },
                size,
                class: className,
            })}
            pointerEvents='auto'
        />
    )
})

const DrawerHeader = React.forwardRef<
    React.ComponentRef<typeof UIDrawer.Header>,
    IDrawerHeaderProps
>(function DrawerHeader({ className, ...props }, ref) {
    return (
        <UIDrawer.Header ref={ref} {...props} className={drawerHeaderStyle({ class: className })} />
    )
})

const DrawerBody = React.forwardRef<React.ComponentRef<typeof UIDrawer.Body>, IDrawerBodyProps>(
    function DrawerBody({ className, ...props }, ref) {
        return (
            <UIDrawer.Body ref={ref} {...props} className={drawerBodyStyle({ class: className })} />
        )
    },
)

const DrawerFooter = React.forwardRef<
    React.ComponentRef<typeof UIDrawer.Footer>,
    IDrawerFooterProps
>(function DrawerFooter({ className, ...props }, ref) {
    return (
        <UIDrawer.Footer ref={ref} {...props} className={drawerFooterStyle({ class: className })} />
    )
})

const DrawerCloseButton = React.forwardRef<
    React.ComponentRef<typeof UIDrawer.CloseButton>,
    IDrawerCloseButtonProps
>(function DrawerCloseButton({ className, ...props }, ref) {
    return (
        <UIDrawer.CloseButton
            ref={ref}
            {...props}
            className={drawerCloseButtonStyle({ class: className })}
        />
    )
})

Drawer.displayName = 'Drawer'
DrawerBackdrop.displayName = 'DrawerBackdrop'
DrawerContent.displayName = 'DrawerContent'
DrawerHeader.displayName = 'DrawerHeader'
DrawerBody.displayName = 'DrawerBody'
DrawerFooter.displayName = 'DrawerFooter'
DrawerCloseButton.displayName = 'DrawerCloseButton'

export {
    Drawer,
    DrawerBackdrop,
    DrawerContent,
    DrawerHeader,
    DrawerBody,
    DrawerFooter,
    DrawerCloseButton,
}
